/*
 * Which layout target a device shows (standalone runtime).
 *
 * A screen has layouts for some targets. The device has a viewport. The
 * best layout is the one whose design surface has the most similar shape
 * (aspect ratio, compared on a log scale so 2:1 and 1:2 are equally far
 * from 1:1), and among equal shapes the one closest in size. A TP8 rotated
 * to portrait gets the portrait layout, a phone in landscape the landscape
 * one, and a screen without a portrait layout still shows its landscape
 * layout, letterboxed, instead of nothing.
 *
 * `?ziel=` in the URL overrides the choice when that layout exists.
 *
 * Pure module, tested in tests/runtimeTarget.test.ts.
 */

import type { LayoutTarget } from '../hmi/widgets';

const FRAMES: Readonly<Record<LayoutTarget, { width: number; height: number }>> = {
  desktopResponsive: { width: 1440, height: 900 },
  panelFixed1024x600: { width: 1024, height: 600 },
  panelFixed1280x800: { width: 1280, height: 800 },
  panelPortrait800x1280: { width: 800, height: 1280 },
  mobileResponsive: { width: 390, height: 844 },
  mobileLandscape: { width: 844, height: 390 },
};

export function pickTarget(
  available: readonly LayoutTarget[],
  viewport: { width: number; height: number },
  requested?: string | null,
): LayoutTarget | null {
  if (available.length === 0) return null;
  if (requested && (available as readonly string[]).includes(requested)) return requested as LayoutTarget;
  const va = Math.log(viewport.width / Math.max(1, viewport.height));
  let best: LayoutTarget | null = null;
  let bestScore = Infinity;
  for (const t of available) {
    const f = FRAMES[t];
    if (!f) continue;
    const shape = Math.abs(va - Math.log(f.width / f.height));
    const size = Math.abs(Math.log(viewport.width / f.width)) * 0.5;
    const score = shape + size;
    if (score < bestScore) { bestScore = score; best = t; }
  }
  return best ?? available[0]!;
}
